import { WEEKDAYS } from "@/components/roster/shiftColours";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const OFF_RULES = [
  { id: "location", title: "Location default", text: "Use the week-off setting saved for this location." },
  { id: "stagger", title: "Auto stagger", text: "Offs are spread so the team isn't off together, never more than 6 working days in a row." },
  { id: "rotate_forward", title: "Rotate forward each week", text: "Each person's off day moves one day later every week (Sun → Mon → Tue …)." },
  { id: "pattern", title: "Repeat a week pattern", text: "Set the off day(s) for each week below; people take the weeks in turn so they aren't all off together." },
];

export function monthWeeks(year, month) {
  const days = new Date(year, month, 0).getDate();
  const names = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  return Array.from({ length: Math.ceil(days / 7) }, (_, i) => {
    const from = i * 7 + 1, to = Math.min(i * 7 + 7, days);
    const wd = Array.from({ length: to - from + 1 }, (_, j) => names[new Date(year, month - 1, from + j).getDay()]);
    return { key: String(i + 1), from, to, weekdays: wd };
  });
}

export function DayChips({ value, onChange, testid, allowed }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {WEEKDAYS.map((d) => {
        const on = value.includes(d);
        const off = allowed && !allowed.includes(d);
        return (
          <button key={d} disabled={off} onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d])} data-testid={`${testid}-${d}`}
            className={`h-9 w-11 rounded-md border text-sm font-semibold capitalize disabled:cursor-not-allowed disabled:opacity-30 ${on ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>{d.slice(0, 3)}</button>
        );
      })}
    </div>
  );
}

export function WeekGrid({ value = {}, onChange, weeks, month, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {weeks.map((w) => (
        <div key={w.key} className="flex flex-wrap items-center gap-3">
          <span className="w-36 text-sm font-semibold text-slate-800">Week {w.key} <span className="font-normal text-slate-500">({w.from}–{w.to} {MON[month - 1]})</span></span>
          <DayChips value={value[w.key] || []} allowed={w.weekdays} onChange={(v) => onChange({ ...value, [w.key]: v })} testid={`${testid}-w${w.key}`} />
        </div>
      ))}
    </div>
  );
}

export function OffRuleCard({ rule, setRule, weeks, month }) {
  const cur = OFF_RULES.find((r) => r.id === (rule.type || "location"));
  return (
    <Card className="space-y-3 p-4" data-testid="off-rule-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-slate-800">Week-off rule for everyone:</span>
        <Select value={rule.type || "location"} onValueChange={(v) => setRule({ ...rule, type: v })}>
          <SelectTrigger className="h-10 w-64" data-testid="off-rule-select"><SelectValue /></SelectTrigger>
          <SelectContent>{OFF_RULES.map((r) => <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>)}</SelectContent>
        </Select>
        {["stagger", "rotate_forward"].includes(rule.type) && (
          <Select value={String(rule.n || 1)} onValueChange={(v) => setRule({ ...rule, n: +v })}>
            <SelectTrigger className="h-10 w-40" data-testid="off-rule-n"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n} off day(s) / week</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <p className="text-sm text-slate-600">{cur?.text}</p>
      {rule.type === "pattern" && <WeekGrid value={rule.pattern || {}} onChange={(p) => setRule({ ...rule, pattern: p })} weeks={weeks} month={month} testid="off-pattern" />}
    </Card>
  );
}
